import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { useParams } from "react-router-dom";
import jwtDecode from "jwt-decode";

import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import CustomTable from "../../../components/table/table";
import TextEditer from "../../../components/inputs/texteditor";
import "./style.css";

import {
  adminBulkEmailDetail,
  adminBulkEmailUpdate,
  superadminBulkEmailDetail,
  superadminBulkEmailApprove,
  superadminBulkEmailReject,
  superadminBulkEmailUpdate,
} from "../admin.api";

const getStatusInfo = (status) => {
  const raw = status || "";
  const value = String(raw).toLowerCase();

  const statusMap = {
    sent: {
      label: "Sent",
      class: "successs",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      ),
    },
    submitted: {
      label: "Submitted",
      class: "info",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path>
        </svg>
      ),
    },
    rejected: {
      label: "Rejected",
      class: "danger",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      ),
    },
  };

  const defaultIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="16" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>
  );

  let key = "secondary";

  if (value.includes("sent")) key = "sent";
  else if (value.includes("submit")) key = "submitted";
  else if (
    value.includes("fail") ||
    value.includes("error") ||
    value.includes("reject")
  )
    key = "rejected";

  return (
    statusMap[key] || {
      label: raw?.charAt(0).toUpperCase() + raw?.slice(1),
      class: "secondary",
      icon: defaultIcon,
    }
  );
};

export default function BulkEmailDetail(props) {
  const { uuid } = useParams();
  const [bulkEmail, setBulkEmail] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsTotalPages, setRecipientsTotalPages] = useState(1);
  const [recipientsPerPage, setRecipientsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("user");

  const [isActive, setIsActive] = useState(true);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertMessage, setRevertMessage] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editEditorKey, setEditEditorKey] = useState(0);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [editSelectedFiles, setEditSelectedFiles] = useState([]);
  const [editExistingAttachments, setEditExistingAttachments] = useState([]);
  const [editFormData, setEditFormData] = useState({
    subject: "",
    body_html: "",
    body_text: "",
    metadata: {
      campaign: "",
      priority: "normal",
    },
  });

  const adminType = Number(jwtDecode(props.state.payload).user_type);
  const baseApiUrl = process.env.REACT_APP_BASE_URL || "";
  const fileBaseUrl = baseApiUrl.endsWith("/api")
    ? baseApiUrl.slice(0, -4) + "/storage"
    : baseApiUrl.replace("/api", "") + "storage";

  const fetchDetail = (page = 1) => {
    if (!uuid) {
      setIsActive(false);
      return;
    }
    setIsActive(true);

    const apiCall =
      adminType === 2
        ? superadminBulkEmailDetail(uuid, page)
        : adminBulkEmailDetail(uuid, page);

    apiCall
      .then((res) => {
        if (res?.success && res.data) {
          const bulk = res.data.bulk_email || null;
          const recipientPager = res.data.recipients || {};
          const recipientData = recipientPager.data || [];
          setBulkEmail(bulk);
          setRecipients(recipientData);
          setRecipientsPage(recipientPager.current_page || page);
          setRecipientsTotalPages(recipientPager.last_page || 1);
        } else {
          setBulkEmail(null);
          setRecipients([]);
          setSnackMessage("Failed to load bulk email details");
          setIsSnackError(true);
          setSnackOpen(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching bulk email detail:", error);
        setBulkEmail(null);
        setRecipients([]);
        setSnackMessage("Failed to load bulk email details");
        setIsSnackError(true);
        setSnackOpen(true);
      })
      .finally(() => {
        setIsActive(false);
      });
  };

  useEffect(() => {
    fetchDetail(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const snackbarClose = (flag) => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
    if (!flag && !bulkEmail) {
      props.history.push("/bulk-emails");
    }
  };

  const handleRecipientsPageChange = (page) => {
    if (page < 1 || page > recipientsTotalPages) return;
    fetchDetail(page);
  };

  const renderRecipientStatus = (status) => {
    const raw = status || "";
    const value = String(raw).toLowerCase();
    let label = raw || "--";
    let cls = "badge bg-secondary";

    if (["sent", "delivered", "success", "successful"].includes(value)) {
      label = "Sent";
      cls = "badge bg-success";
    } else if (
      ["pending", "queued", "processing", "submitted"].includes(value)
    ) {
      label = "Submitted";
      cls = "badge bg-warning text-dark";
    } else if (["failed", "bounced", "error"].includes(value)) {
      label = "Failed";
      cls = "badge bg-danger";
    } else if (["opened", "read"].includes(value)) {
      label = "Opened";
      cls = "badge bg-info text-dark";
    }

    return <span className={`${cls} rounded-0`}>{label}</span>;
  };

  const renderRecipientsPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, recipientsPage - Math.floor(maxVisible / 2));
    let end = Math.min(recipientsTotalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pages.push(
        <button
          key={1}
          className="btn btn-sm btn-outline-primary"
          onClick={() => handleRecipientsPageChange(1)}
        >
          1
        </button>,
      );
      if (start > 2)
        pages.push(
          <span key="start-dots" className="px-1 text-primary">
            ...
          </span>,
        );
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`btn btn-sm ${recipientsPage === i ? "btn-primary text-white" : "btn-outline-primary"}`}
          onClick={() => handleRecipientsPageChange(i)}
        >
          {i}
        </button>,
      );
    }

    if (end < recipientsTotalPages) {
      if (end < recipientsTotalPages - 1)
        pages.push(
          <span key="end-dots" className="px-1 text-primary">
            ...
          </span>,
        );
      pages.push(
        <button
          key={recipientsTotalPages}
          className="btn btn-sm btn-outline-primary"
          onClick={() => handleRecipientsPageChange(recipientsTotalPages)}
        >
          {recipientsTotalPages}
        </button>,
      );
    }

    return pages;
  };

  const handleRecipientsPerPageChange = (e) => {
    setRecipientsPerPage(parseInt(e.target.value));
    setRecipientsPage(1);
  };

  const attachmentUrl = (attachmentPath) => {
    if (!attachmentPath) return "#";
    // Replace escaped slashes with normal slashes and construct URL
    const cleanPath = attachmentPath.replace(/\\/g, "/");
    return `${fileBaseUrl}/${cleanPath}`;
  };

  const normalizedStatus = String(bulkEmail?.status || "").toLowerCase();
  const reviewMessage = (
    bulkEmail?.review_message ??
    bulkEmail?.message ??
    bulkEmail?.reject_message ??
    bulkEmail?.revert_message ??
    ""
  ).trim();
  const isRejectedBulkEmail =
    normalizedStatus.includes("reject") ||
    normalizedStatus.includes("fail") ||
    normalizedStatus.includes("error");
  const canReviewBulkEmail =
    adminType === 2 &&
    ![
      "sent",
      "sending",
      "queued",
      "approved",
      "rejected",
      "reject",
      "reverted",
      "revert",
    ].includes(normalizedStatus);

  const canEditRejectedBulkEmail = Boolean(
    adminType !== 2 && uuid && bulkEmail && isRejectedBulkEmail
  );

  const handleApprove = () => {
    if (!uuid || adminType !== 2) return;
    setIsActive(true);
    superadminBulkEmailApprove(uuid)
      .then((res) => {
        setSnackMessage(res?.message || "Bulk email approved successfully");
        setIsSnackError(false);
        setSnackOpen(true);
        fetchDetail(recipientsPage);
      })
      .catch((error) => {
        setSnackMessage(error?.message || "Failed to approve bulk email");
        setIsSnackError(true);
        setSnackOpen(true);
      })
      .finally(() => {
        setIsActive(false);
      });
  };

  const openRevertModal = () => {
    setRevertMessage("");
    setShowRevertModal(true);
  };

  const openEditModal = () => {
    if (!bulkEmail) return;

    let parsedMetadata = bulkEmail.metadata || {};

    if (typeof parsedMetadata === "string") {
      try {
        parsedMetadata = JSON.parse(parsedMetadata);
      } catch (err) {
        parsedMetadata = {};
      }
    }

    setEditFormData({
      subject: bulkEmail.subject || "",
      body_html: bulkEmail.body_html || "",
      body_text: bulkEmail.body_text || "",
      metadata: {
        campaign: parsedMetadata?.campaign || "",
        priority: parsedMetadata?.priority || "normal",
      },
    });
    setEditExistingAttachments(bulkEmail.attachments || []);
    setEditSelectedFiles([]);
    setFileInputKey((prev) => prev + 1);
    setEditEditorKey((prev) => prev + 1);
    setIsEditModalOpen(true);
  };

  const closeRevertModal = () => {
    if (isActive) return;
    setShowRevertModal(false);
    setRevertMessage("");
  };

  const handleEditModalClose = () => {
    if (isActive) return;
    setIsEditModalOpen(false);
  };

  const handleEditFormChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");

      setEditFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
      return;
    }

    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditFileChange = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024;
    const oversized = files.filter((file) => file.size > maxSize);

    if (oversized.length > 0) {
      setSnackMessage("File size must be less than 5 MB");
      setIsSnackError(true);
      setSnackOpen(true);
      e.target.value = "";
      return;
    }

    setEditSelectedFiles(files);
  };

  const handleEditSubmit = () => {
    if (!uuid) return;

    setIsActive(true);

    const formData = new FormData();
    formData.append("subject", editFormData.subject);
    formData.append("body_html", editFormData.body_html);
    formData.append("body_text", editFormData.body_text);
    formData.append("metadata[campaign]", editFormData.metadata.campaign);
    formData.append("metadata[priority]", editFormData.metadata.priority);

    editSelectedFiles.forEach((file) => {
      formData.append("documents[]", file);
    });

    const updateCall =
      adminType === 2
        ? superadminBulkEmailUpdate(uuid, formData)
        : adminBulkEmailUpdate(uuid, formData);

    updateCall
      .then((res) => {
        setSnackMessage(res?.message || "Bulk email updated successfully");
        setIsSnackError(false);
        setSnackOpen(true);
        setIsEditModalOpen(false);
        fetchDetail(recipientsPage);
      })
      .catch((error) => {
        setSnackMessage(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update bulk email"
        );
        setIsSnackError(true);
        setSnackOpen(true);
      })
      .finally(() => {
        setIsActive(false);
      });
  };

  const handleRevert = () => {
    if (!uuid || adminType !== 2) return;

    const message = revertMessage.trim();

    if (!message) {
      setSnackMessage("Please enter a revert message");
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    setIsActive(true);
    superadminBulkEmailReject(uuid, { message })
      .then((res) => {
        setSnackMessage(res?.message || "Bulk email reverted successfully");
        setIsSnackError(false);
        setSnackOpen(true);
        setShowRevertModal(false);
        setRevertMessage("");
        fetchDetail(recipientsPage);
      })
      .catch((error) => {
        setSnackMessage(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to revert bulk email",
        );
        setIsSnackError(true);
        setSnackOpen(true);
      })
      .finally(() => {
        setIsActive(false);
      });
  };

  const bulkEmailStatusInfo = getStatusInfo(bulkEmail?.status);

  return (
    <div>
      <Loader open={isActive} />
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />

      <div className="view-container">
        <span className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 pt-2">
            <b>Bulk Email Details</b>
          </h6>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => props.history.push("/bulk-emails")}
          >
            Back
          </button>
        </span>
        <hr />

        {!bulkEmail && !isActive && (
          <div className="alert alert-warning">Details not available.</div>
        )}

        {bulkEmail && (
          <>
            <ul
              className="nav nav-tabs border-0"
              style={{ borderBottom: "none" }}
            >
              <li className="nav-item">
                <button
                  className={`nav-link fw-bold ${activeTab === "user" ? "text-primary" : "text-muted"}`}
                  type="button"
                  onClick={() => setActiveTab("user")}
                  style={{
                    border: "none",
                    borderBottom:
                      activeTab === "user"
                        ? "3px solid #0d6efd"
                        : "3px solid transparent",
                    borderRadius: 0,
                    backgroundColor: "transparent",
                  }}
                >
                  Message Details
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link fw-bold ${activeTab === "recipients" ? "text-primary" : "text-muted"}`}
                  type="button"
                  onClick={() => setActiveTab("recipients")}
                  style={{
                    border: "none",
                    borderBottom:
                      activeTab === "recipients"
                        ? "3px solid #0d6efd"
                        : "3px solid transparent",
                    borderRadius: 0,
                    backgroundColor: "transparent",
                  }}
                >
                  Recipient Details
                </button>
              </li>
            </ul>

            {activeTab === "user" && (
              <div className="pt-3">
                <div className="data">
                  <div className="row">
                    <div className="col-3">Subject</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.subject}</div>
                  </div>
                  <div className="row align-items-start">
                    <div className="col-3">Body</div>
                    <div className="col-1">:</div>
                    <div className="col-7">
                      <div
                        className="border rounded p-2"
                        dangerouslySetInnerHTML={{
                          __html: bulkEmail.body_html,
                        }}
                      />
                    </div>
                  </div>
                  {Array.isArray(bulkEmail.attachments) &&
                    bulkEmail.attachments.length > 0 && (
                      <div className="row align-items-start">
                        <div className="col-3">Attachments</div>
                        <div className="col-1">:</div>
                        <div className="col-7 break-word">
                          <div className="d-flex flex-column gap-1">
                            {bulkEmail.attachments.map((att, index) => (
                              <div key={`${att.path}-${index}`}>
                                <a
                                  href={attachmentUrl(att.path)}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {att.name}
                                </a>
                                <small className="text-muted ms-2">
                                  ({att.mime}, {att.size} bytes)
                                </small>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  <div className="row">
                    <div className="col-3">User Category</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">
                      {bulkEmail.user_category}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-3">Status</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">
                      <span
                        className={`status-badge ${bulkEmailStatusInfo.class}`}
                      >
                        <span className="status-icon">
                          {bulkEmailStatusInfo.icon}
                        </span>
                        <span className="status-label">
                          {bulkEmailStatusInfo.label}
                        </span>
                      </span>
                    </div>
                  </div>
                  {bulkEmail.metadata?.campaign && (
                    <div className="row">
                      <div className="col-3">Campaign</div>
                      <div className="col-1">:</div>
                      <div className="col-7 break-word">
                        {bulkEmail.metadata.campaign}
                      </div>
                    </div>
                  )}
                  <div className="row">
                    <div className="col-3">Priority</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">
                      {bulkEmail.metadata?.priority ?? "--"}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-3">Submitted At</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">
                      {bulkEmail.submitted_at ? moment(bulkEmail.submitted_at).format("DD-MM-YYYY hh:mm:ss A") : "--"}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-3">Approved At</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">
                      {bulkEmail.approved_at ? moment(bulkEmail.approved_at).format("DD-MM-YYYY hh:mm:ss A") : "--"}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-3">Total Recipients</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">
                      {bulkEmail.total_recipients}
                    </div>
                  </div>
                  {reviewMessage && (
                    <div className="row align-items-start">
                      <div className="col-3">Revert Message</div>
                      <div className="col-1">:</div>
                      <div className="col-7 break-word">{reviewMessage}</div>
                    </div>
                  )}

                  {/* <div className="row">
                    <div className="col-3">Sent</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.sent_recipients}</div>
                  </div> */}
                  {bulkEmail.creator && (
                    <>
                      <div className="row">
                        <div className="col-3">Creator Name</div>
                        <div className="col-1">:</div>
                        <div className="col-7 break-word">
                          {bulkEmail.creator.name}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-3">Creator Email</div>
                        <div className="col-1">:</div>
                        <div className="col-7 break-word">
                          {bulkEmail.creator.email}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {(canReviewBulkEmail || canEditRejectedBulkEmail) && (
                  <div className="d-flex justify-content-center mt-4">
                    {canEditRejectedBulkEmail && (
                      <button
                        type="button"
                        className="btn btn-outline-info px-4 me-2"
                        onClick={openEditModal}
                        disabled={isActive}
                      >
                        Edit
                      </button>
                    )}
                    {canReviewBulkEmail && (
                      <>
                        <button
                          type="button"
                          className="btn btn-outline-danger px-4 me-2"
                          onClick={openRevertModal}
                          disabled={isActive}
                        >
                          Revert
                        </button>
                        <button
                          type="button"
                          className="btn btn-success px-4"
                          onClick={handleApprove}
                          disabled={isActive}
                        >
                          Approve
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "recipients" && (
              <div className="pt-3">
                <div className="mb-3">
                  <h6>
                    <b>Recipients</b>
                  </h6>
                  <CustomTable
                    columns={[
                      { title: "Name", field: "name" },
                      { title: "Recipient Type", field: "recipient_type" },
                      { title: "Email", field: "email" },
                      {
                        title: "Status",
                        render: (row) => renderRecipientStatus(row.send_status),
                      },
                    ]}
                    data={recipients}
                    paging={false}
                  />
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="flex-grow-1"></div>
                    <div className="btn-group">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={recipientsPage === 1}
                        onClick={() =>
                          handleRecipientsPageChange(recipientsPage - 1)
                        }
                      >
                        Prev
                      </button>
                      {renderRecipientsPagination()}
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={recipientsPage === recipientsTotalPages}
                        onClick={() =>
                          handleRecipientsPageChange(recipientsPage + 1)
                        }
                      >
                        Next
                      </button>
                    </div>
                    <div className="flex-grow-1 d-flex justify-content-end">
                      <select
                        className="form-control form-control-sm w-auto"
                        value={recipientsPerPage}
                        onChange={handleRecipientsPerPageChange}
                        style={{ height: "31px", fontSize: "8px" }}
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        show={showRevertModal}
        onHide={closeRevertModal}
        centered
        backdrop="static"
      >
        {/* <Modal.Header closeButton={!isActive}> */}
        <Modal.Title>Revert Bulk Email</Modal.Title>
        {/* </Modal.Header> */}
        <Modal.Body>
          <div className="mb-2">
            <label className="form-label fw-bold">
              Message <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              rows={4}
              value={revertMessage}
              onChange={(e) => setRevertMessage(e.target.value)}
              placeholder="Enter the reason to revert this mail to admin"
              disabled={isActive}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeRevertModal}
            disabled={isActive}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleRevert}
            disabled={isActive}
          >
            Submit
          </button>
        </Modal.Footer>
      </Modal>

      {isEditModalOpen && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "1rem",
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "100%",
              maxWidth: "900px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div className="modal-header bg-primary text-white p-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Edit Email</h5>
              <button
                type="button"
                className="btn-close text-white"
                onClick={handleEditModalClose}
                style={{ background: "none", border: "none", fontSize: "24px" }}
                disabled={isActive}
              >
                x
              </button>
            </div>

            <div className="modal-body p-4">
              <div className="mb-3">
                <label className="form-label">
                  Subject <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editFormData.subject}
                  onChange={(e) =>
                    handleEditFormChange("subject", e.target.value)
                  }
                  placeholder="Enter email subject"
                />
              </div>

              <div className="mb-3">
                <TextEditer
                  label={
                    <>
                      Message <span className="text-danger">*</span>
                    </>
                  }
                  key={`detail-edit-html-${editEditorKey}`}
                  value={editFormData.body_html}
                  onChange={(value) => handleEditFormChange("body_html", value)}
                  required
                />
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Campaign</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editFormData.metadata.campaign}
                    onChange={(e) =>
                      handleEditFormChange("metadata.campaign", e.target.value)
                    }
                    placeholder="Campaign name"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-select"
                    value={editFormData.metadata.priority}
                    onChange={(e) =>
                      handleEditFormChange("metadata.priority", e.target.value)
                    }
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Attachments</label>

                {editExistingAttachments.length > 0 && (
                  <div className="mb-2">
                    <small className="text-muted">Existing attachments:</small>
                    {editExistingAttachments.map((att, index) => (
                      <div key={index} className="mt-1">
                        <small className="text-success">
                          {att.name} ({(att.size / 1024).toFixed(2)} KB)
                        </small>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  key={fileInputKey}
                  type="file"
                  className="form-control"
                  multiple
                  onChange={handleEditFileChange}
                />

                {editSelectedFiles.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted">Files to upload:</small>
                    {editSelectedFiles.map((file, index) => (
                      <div key={index} className="mt-1">
                        <small className="text-primary">
                          {file.name} ({(file.size / 1024).toFixed(2)} KB)
                        </small>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer p-3 border-top d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary w-25"
                onClick={handleEditModalClose}
                disabled={isActive}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn btn-secondary w-25"
                onClick={handleEditSubmit}
                disabled={!editFormData.subject || !editFormData.body_html || isActive}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
